'use client';

import { useLocale } from 'next-intl';
// next-intl's usePathname, NOT next/navigation's: the raw one leaks the
// middleware-rewritten internal path ("/pl/...") after client-side locale
// switches, which stacked prefixes into "/ru/pl/..." → 404.
import { usePathname } from '@/navigation';
import { useState, useRef, useEffect } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { clsx } from 'clsx';
import { sendGAEvent } from '@next/third-parties/google';

const LOCALES = [
  { code: 'pl', label: 'PL' },
  { code: 'en', label: 'EN' },
  { code: 'ua', label: 'UA' },
  { code: 'ru', label: 'RU' },
] as const;

export default function LanguageSwitcher() {
  const locale = useLocale();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  // Exact per-locale URLs from the page's own <link rel="alternate" hreflang>
  // tags. Matters on blog articles, where slugs are translated per locale —
  // pathForLocale would keep the current slug and land on a 301. Populated
  // after mount; until then the pathForLocale fallback below still works.
  const [alternates, setAlternates] = useState<Record<string, string>>({});
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const map: Record<string, string> = {};
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => {
        const hreflang = el.getAttribute('hreflang');
        const href = el.getAttribute('href');
        if (!hreflang || hreflang === 'x-default' || !href) return;
        // Keep only the path: metadata hreflang URLs are absolute against
        // SITE_URL (the prod domain), so using them verbatim would navigate
        // dev/preview hosts to prod. Same-origin path works everywhere.
        try {
          const url = new URL(href, window.location.origin);
          map[hreflang] = url.pathname + url.search;
        } catch {
          map[hreflang] = href;
        }
      });
    setAlternates(map);
  }, [pathname]);

  // Close on outside click / Escape.
  useEffect(() => {
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Target URL for a locale (strip current prefix, add target; pl has none).
  // next-intl's usePathname already returns a locale-less path, but strip
  // defensively anyway — any locale segment (pl included), whole-segment match
  // only, so "/entrance" is never mistaken for "/en".
  function pathForLocale(targetLocale: string) {
    let newPath = pathname;
    for (const { code } of LOCALES) {
      if (newPath === `/${code}` || newPath.startsWith(`/${code}/`)) {
        newPath = newPath.slice(`/${code}`.length) || '/';
        break;
      }
    }
    const prefix = targetLocale === 'pl' ? '' : `/${targetLocale}`;
    // Root path gets no trailing slash after the prefix ("/en", not "/en/") —
    // these hrefs are crawled now, and "/en/" would 308-redirect to "/en".
    const suffix = newPath === '/' ? '' : newPath.startsWith('/') ? newPath : `/${newPath}`;
    return prefix + suffix || '/';
  }

  // SEO contract: the locale links are plain <a href> that must exist in the
  // server HTML at all times (the list below is hidden with CSS, never
  // conditionally rendered) and navigation happens by following the href —
  // no preventDefault/router.push. The click handler only persists the choice
  // before the browser navigates.
  function onPick(e: ReactMouseEvent, targetLocale: string) {
    if (targetLocale === locale) {
      // Same locale — just close the dropdown, don't reload the page.
      e.preventDefault();
      setOpen(false);
      return;
    }

    document.cookie = `NEXT_LOCALE=${targetLocale};path=/;max-age=${365 * 24 * 60 * 60}`;
    localStorage.setItem('NEXT_LOCALE', targetLocale);

    // GA: explicit site-language switch (distinct from GA's auto browser `language`).
    sendGAEvent('event', 'language_change', { from: locale, to: targetLocale });

    setOpen(false);
  }

  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
        className="flex items-center gap-1.5 rounded-full border border-outline-variant/60 px-3 py-1.5 text-label-caps font-headline font-semibold uppercase tracking-widest text-on-surface transition-colors hover:border-primary hover:text-primary"
      >
        {current.label}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={clsx('transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Always in the DOM; `invisible` (visibility:hidden) when closed removes
          it from the a11y tree and tab order but keeps the hrefs crawlable. */}
      <ul
        role="listbox"
        aria-hidden={!open}
        className={clsx(
          'absolute right-0 z-50 mt-2 w-full overflow-hidden rounded-md border border-outline-variant/40 bg-surface-container/95 py-1 backdrop-blur-xl',
          !open && 'invisible'
        )}
      >
        {LOCALES.map(({ code, label }) => (
          <li key={code}>
            <a
              href={alternates[code === 'ua' ? 'uk' : code] ?? pathForLocale(code)}
              role="option"
              aria-selected={locale === code}
              hrefLang={code === 'ua' ? 'uk' : code}
              onClick={(e) => onPick(e, code)}
              className={clsx(
                'block w-full px-4 py-2 text-left text-label-caps font-headline font-semibold uppercase tracking-widest transition-colors hover:bg-surface-container-high',
                locale === code ? 'text-primary' : 'text-on-surface-variant'
              )}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
